import React from 'react';
import classNames from 'classnames';
import { titleize } from 'inflection';

import Link from '@buyside/components/presentation/Link/link';
import { IdeaResearchItemProps } from '@buyside/components/presentation/ResearchItem/researchItem';

import DownArrow from '@buyside/svgs/down_arrow.svg';
import DownTriangle from '@buyside/svgs/triangle_down.svg';
import UpArrow from '@buyside/svgs/up_arrow.svg';
import UpTriangle from '@buyside/svgs/triangle_up.svg';
import RedactedDataPoint from '@buyside/components/presentation/AccessControl/redactedDataPoint';

import formatNumber, { roundNumber } from '@buyside/util/formatNumber';
import { ideaUrl } from '@buyside/util/urlHelper';

import styles from './researchPositionHeaderStyles.scss';

export type ResearchPositionHeaderProps = Pick<
  IdeaResearchItemProps,
  'canCurrentUserViewIdeaFilterDetails' | 'closedAt' | 'expectedReturn' | 'totalReturn'
> & {
  ideaId: string;
  isPair: boolean;
  position: IdeaResearchItemProps['positions'][number];
  narrow?: boolean;
};

const IdeaResearchItemHeader: React.FC<ResearchPositionHeaderProps> = ({
  canCurrentUserViewIdeaFilterDetails,
  closedAt,
  expectedReturn,
  ideaId,
  isPair,
  narrow,
  position,
  totalReturn
}): React.ReactElement => {
  const returnTitle = closedAt ? 'Realized Return' : `${isPair ? 'Pair' : ''} Return To Date`;

  const ReturnTriangle = totalReturn && (totalReturn < 0 ? DownTriangle : UpTriangle);
  const PositionArrow = position.direction === 'long' ? UpArrow : DownArrow;

  return (
    <div className={classNames(styles.researchPositionHeader, { [styles.narrow]: narrow })}>
      <div className={styles.researchPositionTitle}>
        <Link analyticsTag="view-idea" to={ideaUrl(ideaId)}>
          {titleize(position.asset?.name || '')}
        </Link>
        <div>
          <span className={styles.primarySymbol}>{position.asset?.primarySymbol}</span>
          {canCurrentUserViewIdeaFilterDetails ? (
            <span className={styles.direction}>
              <span
                className={classNames(styles.direction, {
                  [styles.red]: position.direction === 'short',
                  [styles.pendingText]: !position.direction
                })}
              >
                {position.direction ? (
                  <>
                    <PositionArrow className={styles.arrowIcon} />
                    {position.direction.toUpperCase()}
                  </>
                ) : (
                  'Pending Direction...'
                )}
              </span>
            </span>
          ) : (
            <RedactedDataPoint Wrapper="span" />
          )}
        </div>
      </div>
      <div className={styles.dataPoints}>
        {!narrow && position.asset && (
          <>
            <div>
              <div className={styles.dataPointName}>Asset Class</div>
              <div className={styles.dataPointValue}>{position.asset.assetClassName}</div>
            </div>
            <div>
              <div className={styles.dataPointName}>Market Cap</div>
              <div className={styles.dataPointValue}>
                {position.asset.fundamental?.marketCap
                  ? `${formatNumber(position.asset.fundamental.marketCap, false, 1)} USD`
                  : 'N/A'}
              </div>
            </div>
          </>
        )}
        <div>
          <div className={styles.dataPointName}>{returnTitle}</div>
          {totalReturn ? (
            <div
              className={classNames(styles.numbers, styles.dataPointValue, {
                [styles.red]: totalReturn < 0
              })}
            >
              <ReturnTriangle className={styles.arrowIcon} />
              {roundNumber(totalReturn, 1)}%
            </div>
          ) : (
            <div className={classNames(styles.pendingText, styles.dataPointValue)}>Pending...</div>
          )}
        </div>
        <div>
          {!closedAt ? (
            <>
              <div className={styles.dataPointName}>Expected Return</div>
              {canCurrentUserViewIdeaFilterDetails ? (
                <div id="expected-return">
                  {expectedReturn ? `${roundNumber(expectedReturn, 1)}%` : 'N/A'}
                </div>
              ) : (
                <RedactedDataPoint Wrapper="div" />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default IdeaResearchItemHeader;
